import React, { useMemo } from 'react'

export function resolveHuayPromoBrandLabel(siteName?: string): string {
  const raw = (siteName || '').trim()
  if (!raw) return 'แทงหวย'
  const primary = raw.split('::')[0]?.split(' - ')[0]?.split(' – ')[0]?.trim()
  return primary || raw
}

function getBadgeLayout(label: string): { badgeX: number; badgeWidth: number; fontSize: number; textX: number } {
  const len = [...label].length
  const fontSize = len > 14 ? 8 : len > 10 ? 9 : 11
  const charWidth = fontSize * 0.62
  const pad = 18
  const badgeWidth = Math.min(176, Math.max(72, Math.ceil(len * charWidth) + pad))
  const textX = 160
  const badgeX = textX - badgeWidth / 2
  return { badgeX, badgeWidth, fontSize, textX }
}

export interface HuayLobbyPromoArtProps {
  /** ชื่อแบรนด์จาก tenant theme (theme.branding.siteName) */
  brandLabel?: string
}

/** ภาพประกอบแท็บ "หวย" ใน lobby-casino — SVG โทนมืด + ทอง */
const HuayLobbyPromoArt: React.FC<HuayLobbyPromoArtProps> = ({ brandLabel }) => {
  const label = resolveHuayPromoBrandLabel(brandLabel)
  const badge = useMemo(() => getBadgeLayout(label), [label])

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 320 220"
      role="img"
      aria-label={`แทงหวยออนไลน์ ${label}`}
      style={{ width: '100%', maxWidth: 280, height: 'auto', display: 'block' }}
    >
      <defs>
        <linearGradient id="huayPromoGold" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fcd34d" />
          <stop offset="50%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
        <linearGradient id="huayPromoCard" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1e3a5f" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>
        <linearGradient id="huayPromoGlow" x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="var(--htd-accent)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--htd-accent)" stopOpacity="0" />
        </linearGradient>
        <filter id="huayPromoShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#000" floodOpacity="0.45" />
        </filter>
        <filter id="huayPromoGlowFilter">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <ellipse cx="160" cy="118" rx="130" ry="72" fill="url(#huayPromoGlow)" />
      <circle cx="52" cy="48" r="28" fill="#f59e0b" opacity="0.12" />
      <circle cx="268" cy="56" r="22" fill="var(--htd-accent)" opacity="0.14" />

      <g filter="url(#huayPromoShadow)">
        <rect x="48" y="42" width="224" height="136" rx="18" fill="url(#huayPromoCard)" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
        <path
          d="M48 78 H272 M48 118 H272"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="1"
          strokeDasharray="6 6"
        />
        <circle cx="48" cy="78" r="6" fill="#0b1220" stroke="rgba(255,255,255,0.06)" />
        <circle cx="48" cy="118" r="6" fill="#0b1220" stroke="rgba(255,255,255,0.06)" />
        <circle cx="272" cy="78" r="6" fill="#0b1220" stroke="rgba(255,255,255,0.06)" />
        <circle cx="272" cy="118" r="6" fill="#0b1220" stroke="rgba(255,255,255,0.06)" />
      </g>

      <rect
        x={badge.badgeX}
        y={54}
        width={badge.badgeWidth}
        height={18}
        rx={9}
        fill="url(#huayPromoGold)"
        opacity={0.95}
      />
      <text
        x={badge.textX}
        y={67}
        textAnchor="middle"
        fill="#1e293b"
        fontSize={badge.fontSize}
        fontWeight="700"
        fontFamily="system-ui, sans-serif"
      >
        {label}
      </text>

      <g filter="url(#huayPromoGlowFilter)">
        <circle cx="108" cy="148" r="22" fill="url(#huayPromoGold)" />
        <text x="108" y="154" textAnchor="middle" fill="#1e293b" fontSize="16" fontWeight="800" fontFamily="system-ui, sans-serif">
          8
        </text>
        <circle cx="160" cy="148" r="22" fill="url(#huayPromoGold)" />
        <text x="160" y="154" textAnchor="middle" fill="#1e293b" fontSize="16" fontWeight="800" fontFamily="system-ui, sans-serif">
          6
        </text>
        <circle cx="212" cy="148" r="22" fill="url(#huayPromoGold)" />
        <text x="212" y="154" textAnchor="middle" fill="#1e293b" fontSize="16" fontWeight="800" fontFamily="system-ui, sans-serif">
          9
        </text>
      </g>

      <path
        d="M248 62 l3 7 7 1 -5 5 1 7 -6 -3 -6 3 1 -7 -5 -5 7 -1 z"
        fill="#fcd34d"
        opacity="0.9"
      />
      <path
        d="M78 96 l2 5 5 1 -4 4 1 5 -4 -2 -4 2 1 -5 -4 -4 5 -1 z"
        fill="#93c5fd"
        opacity="0.75"
      />

      <circle cx="236" cy="132" r="18" fill="#fbbf24" stroke="#fde68a" strokeWidth="2" opacity="0.95" />
      <text x="236" y="138" textAnchor="middle" fill="#78350f" fontSize="14" fontWeight="800" fontFamily="system-ui, sans-serif">
        ฿
      </text>
    </svg>
  )
}

export default HuayLobbyPromoArt
